/**
 * Admin Login Page - Solo per amministratori
 * Gli altri utenti si loggano tramite Pacman per la classifica
 */

import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/shared/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/shared/components/ui/card";
import { ThemeToggle } from "@/shared/components/theme/theme-toggle";
import { Shield, Crown, ArrowLeft } from "lucide-react";
import { apiUrl, API_ENDPOINTS, getApiInfo } from "@/lib/api";

export const AdminLogin = () => {
    const [isLoading, setIsLoading] = useState(false);

    const handleDiscordLogin = async () => {
        setIsLoading(true);
        try {
            // Usa configurazione API environment-aware
            const apiInfo = getApiInfo();
            console.log("🔐 API Info:", apiInfo);

            const redirectUri = encodeURIComponent(
                `${window.location.origin}/auth/discord/callback`,
            );
            const loginUrl = apiUrl(
                `${API_ENDPOINTS.AUTH_DISCORD_LOGIN}?redirect_uri=${redirectUri}`,
            );
            console.log("🔐 Calling Discord login API:", loginUrl);

            const response = await fetch(loginUrl);
            console.log(
                "📡 Response status:",
                response.status,
                response.statusText,
            );
            console.log(
                "📡 Response headers:",
                response.headers.get("content-type"),
            );

            const text = await response.text();
            console.log("📡 Response text:", text.substring(0, 200));

            let data;
            try {
                data = JSON.parse(text);
            } catch {
                console.error(
                    "❌ JSON parse error. Response was not JSON:",
                    text.substring(0, 500),
                );
                alert(
                    "Errore: Il server non ha risposto correttamente. Verifica la console per dettagli.",
                );
                return;
            }

            if (data.auth_url) {
                console.log("✅ Redirecting to Discord:", data.auth_url);
                window.location.href = data.auth_url;
            } else {
                console.error("❌ No auth URL received:", data);
                alert("Errore: URL di autenticazione Discord non ricevuto");
            }
        } catch (error) {
            console.error("❌ Admin login error:", error);
            alert(`Errore di connessione: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-background via-red-500/10 to-background flex items-center justify-center p-4">
            {/* Theme Toggle */}
            <div className="absolute top-4 right-4">
                <ThemeToggle />
            </div>

            {/* Back to Home */}
            <div className="absolute top-4 left-4">
                <Button
                    variant="outline"
                    onClick={() => (window.location.href = "/")}
                >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Torna alla Home
                </Button>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="w-full max-w-md"
            >
                <Card className="bg-card/50 border-border/50 backdrop-blur-sm border-red-500/20">
                    <CardHeader className="text-center">
                        <div className="flex justify-center mb-4">
                            <div className="relative">
                                <img
                                    src="/logo.png"
                                    alt="Un Mondo Marrano"
                                    className="h-16 w-16 rounded-full"
                                />
                                <Crown className="absolute -top-2 -right-2 h-6 w-6 text-yellow-500" />
                            </div>
                        </div>
                        <CardTitle className="text-2xl font-bold text-red-400 flex items-center justify-center gap-2">
                            <Shield className="h-6 w-6" />
                            Admin Login
                        </CardTitle>
                        <CardDescription>
                            Accesso riservato agli amministratori del server
                        </CardDescription>
                    </CardHeader>

                    <CardContent className="space-y-4">
                        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-400">
                            ⚠️ Solo utenti con ruolo Founders su Discord possono
                            accedere
                        </div>

                        <Button
                            onClick={handleDiscordLogin}
                            disabled={isLoading}
                            className="w-full bg-[#5865F2] hover:bg-[#4752C4] text-white"
                            size="lg"
                        >
                            {isLoading ? (
                                <div className="flex items-center gap-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    Connessione...
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <svg
                                        className="w-5 h-5"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                    >
                                        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z" />
                                    </svg>
                                    Accedi con Discord
                                </div>
                            )}
                        </Button>

                        <div className="text-center">
                            <div className="text-xs text-muted-foreground mb-2">
                                Per utenti normali:
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => (window.location.href = "/")}
                            >
                                🎮 Gioca a Pacman per entrare in classifica
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>
        </div>
    );
};
